import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Edit, Trash2, DollarSign, Calendar, FileText, Link as LinkIcon, ExternalLink, Mail, Phone, Download, User, UserPlus, Copy, Shield, Plus, X, RefreshCw, Pencil, Clock, ChevronDown, ChevronUp, Check, TrendingUp, Target, Camera, Share2, Video, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import api, { resolveFileUrl } from '../utils/api';

// Helper function to format date as DD/MM/YYYY
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const PlayerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteToken, setInviteToken] = useState('');
  const [inviting, setInviting] = useState(false);
  const [clubContacts, setClubContacts] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactForm, setContactForm] = useState({ club_id: '', outcome: 'potential', contact_date: '', notes: '' });
  const [editingNotes, setEditingNotes] = useState(null);
  const [editNotesValue, setEditNotesValue] = useState('');
  const [expandedHistory, setExpandedHistory] = useState(null);
  const [renegotiations, setRenegotiations] = useState([]);
  const [completedDeals, setCompletedDeals] = useState([]);
  const [showDealForm, setShowDealForm] = useState(false);
  const [dealForm, setDealForm] = useState({ deal_type: 'summer_deal', club_name: '', notes: '' });
  const [showRegoForm, setShowRegoForm] = useState(false);
  const [regoForm, setRegoForm] = useState({ club_name: '', status: 'start_conversation', notes: '' });
  const [editingCareer, setEditingCareer] = useState(false);
  const [careerForm, setCareerForm] = useState({ career_path_1yr: '', career_path_2yr: '', career_path_3yr: '' });
  const [playerMedia, setPlayerMedia] = useState([]);
  const [playerFixtures, setPlayerFixtures] = useState([]);
  const [showMediaForm, setShowMediaForm] = useState(false);
  const [mediaForm, setMediaForm] = useState({ name: '', url: '', media_type: 'clip_link' });
  const [editingMedia, setEditingMedia] = useState(null);

  const fetchPlayer = useCallback(async () => {
    try {
      const response = await api.get(`/players/${id}`);
      setPlayer(response.data);
      setCareerForm({ career_path_1yr: response.data.career_path_1yr || '', career_path_2yr: response.data.career_path_2yr || '', career_path_3yr: response.data.career_path_3yr || '' });
    } catch (error) {
      toast.error('Failed to load player details');
      navigate('/players');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchClubContacts = useCallback(async () => {
    try {
      const res = await api.get(`/player-club-contacts/${id}`);
      setClubContacts(res.data);
    } catch (error) { console.error('Failed to load club contacts:', error); }
  }, [id]);

  const fetchClubs = useCallback(async () => {
    try {
      const res = await api.get('/clubs');
      setClubs(res.data);
    } catch (error) { console.error('Failed to load clubs:', error); }
  }, []);

  const fetchRenegotiations = useCallback(async () => {
    try {
      const res = await api.get(`/contract-renegotiations/${id}`);
      setRenegotiations(res.data);
    } catch (error) { console.error('Failed to load renegotiations:', error); }
  }, [id]);

  const fetchDeals = useCallback(async () => {
    try {
      const res = await api.get(`/completed-deals/${id}`);
      setCompletedDeals(res.data);
    } catch (error) { console.error('Failed to load deals:', error); }
  }, [id]);

  const fetchMedia = useCallback(async () => {
    try {
      const res = await api.get(`/player-portal/media/${id}`);
      setPlayerMedia(res.data);
    } catch (error) { console.error('Failed to load media:', error); }
  }, [id]);

  const fetchPlayerFixtures = useCallback(async () => {
    try {
      const res = await api.get(`/fixtures-diary/player/${id}`);
      setPlayerFixtures(res.data);
    } catch (error) { console.error('Failed to load fixtures:', error); }
  }, [id]);

  useEffect(() => {
    fetchPlayer();
    fetchClubContacts();
    fetchClubs();
    fetchRenegotiations();
    fetchDeals();
    fetchMedia();
    fetchPlayerFixtures();
  }, [id, fetchPlayer, fetchClubContacts, fetchClubs, fetchRenegotiations, fetchDeals, fetchMedia, fetchPlayerFixtures]);

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this player?')) return;

    setDeleting(true);
    try {
      await api.delete(`/players/${id}`);
      toast.success('Player deleted successfully');
      navigate('/players');
    } catch (error) {
      toast.error('Failed to delete player');
      setDeleting(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail) { toast.error('Please enter the player\'s email'); return; }
    setInviting(true);
    try {
      const res = await api.post('/admin/invite-player', { player_id: id, email: inviteEmail });
      setInviteToken(res.data.invite_token);
      toast.success('Invite created! Share the code with the player.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invite');
    } finally {
      setInviting(false);
    }
  };

  const copyInviteCode = () => {
    navigator.clipboard.writeText(inviteToken);
    toast.success('Invite code copied to clipboard');
  };

  const addClubContact = async () => {
    if (!contactForm.club_id) { toast.error('Select a club'); return; }
    try {
      const res = await api.post('/player-club-contacts', { ...contactForm, player_id: id });
      setClubContacts([res.data, ...clubContacts]);
      setContactForm({ club_id: '', outcome: 'potential', contact_date: '', notes: '' });
      setShowContactForm(false);
      toast.success('Club contact added');
    } catch (error) {
      toast.error('Failed to add club contact');
    }
  };

  const updateContactOutcome = async (contactId, outcome) => {
    try {
      const contact = clubContacts.find(c => c.id === contactId);
      await api.put(`/player-club-contacts/${contactId}`, { ...contact, outcome, player_id: id, club_id: contact.club_id });
      setClubContacts(clubContacts.map(c => c.id === contactId ? { ...c, outcome } : c));
      toast.success('Outcome updated');
    } catch { toast.error('Failed to update'); }
  };

  const deleteClubContact = async (contactId) => {
    try {
      await api.delete(`/player-club-contacts/${contactId}`);
      setClubContacts(clubContacts.filter(c => c.id !== contactId));
      toast.success('Deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const saveContactNotes = async (contactId) => {
    try {
      const contact = clubContacts.find(c => c.id === contactId);
      const res = await api.put(`/player-club-contacts/${contactId}`, {
        ...contact, notes: editNotesValue, player_id: id, club_id: contact.club_id
      });
      setClubContacts(clubContacts.map(c => c.id === contactId ? res.data : c));
      setEditingNotes(null);
      toast.success('Notes updated');
    } catch { toast.error('Failed to update notes'); }
  };

  const addDeal = async () => {
    if (!dealForm.club_name) { toast.error('Club name is required'); return; }
    try {
      const res = await api.post('/completed-deals', { ...dealForm, player_id: id });
      setCompletedDeals([res.data, ...completedDeals]);
      setDealForm({ deal_type: 'summer_deal', club_name: '', notes: '' });
      setShowDealForm(false);
      toast.success('Deal added');
    } catch { toast.error('Failed to add deal'); }
  };

  const deleteDeal = async (dealId) => {
    try {
      await api.delete(`/completed-deals/${dealId}`);
      setCompletedDeals(completedDeals.filter(d => d.id !== dealId));
      toast.success('Deal removed');
    } catch { toast.error('Failed to delete deal'); }
  };

  const addRenegotiation = async () => {
    if (!regoForm.club_name) { toast.error('Enter the club name'); return; }
    try {
      const res = await api.post('/contract-renegotiations', { ...regoForm, player_id: id });
      setRenegotiations([res.data, ...renegotiations]);
      setRegoForm({ club_name: '', status: 'start_conversation', notes: '' });
      setShowRegoForm(false);
      toast.success('Renegotiation added');
    } catch { toast.error('Failed to add'); }
  };

  const saveCareerPath = async () => {
    try {
      await api.put(`/players/${id}`, { ...player, ...careerForm });
      setPlayer({ ...player, ...careerForm });
      setEditingCareer(false);
      toast.success('Career path saved');
    } catch { toast.error('Failed to save career path'); }
  };

  const addMedia = async () => {
    if (!mediaForm.url && mediaForm.media_type === 'clip_link') { toast.error('Enter a URL'); return; }
    try {
      const res = await api.post('/player-portal/media', { ...mediaForm, player_id: id });
      setPlayerMedia([res.data, ...playerMedia]);
      setMediaForm({ name: '', url: '', media_type: 'clip_link' });
      setShowMediaForm(false);
      toast.success('Media added');
    } catch { toast.error('Failed to add media'); }
  };

  const updateMedia = async () => {
    if (!editingMedia) return;
    try {
      const res = await api.put(`/player-portal/media/${editingMedia.id}`, { ...mediaForm, player_id: id });
      setPlayerMedia(playerMedia.map(m => m.id === editingMedia.id ? res.data : m));
      setEditingMedia(null);
      setMediaForm({ name: '', url: '', media_type: 'clip_link' });
      setShowMediaForm(false);
      toast.success('Media updated');
    } catch { toast.error('Failed to update media'); }
  };

  const deleteMedia = async (mediaId) => {
    if (!window.confirm('Delete this media?')) return;
    try {
      await api.delete(`/player-portal/media/${mediaId}`);
      setPlayerMedia(playerMedia.filter(m => m.id !== mediaId));
      toast.success('Media deleted');
    } catch { toast.error('Failed to delete media'); }
  };

  const shareMedia = (media) => {
    const shareUrl = media.url || '';
    const shareText = `${player?.name || 'Player'} - ${media.name || 'Media'}: ${shareUrl}`;
    if (navigator.share) {
      navigator.share({ title: media.name, text: shareText, url: shareUrl }).catch(() => {});
    } else {
      navigator.clipboard.writeText(shareText);
      toast.success('Link copied to clipboard');
    }
  };

  const updateRegoStatus = async (regoId, status) => {
    try {
      const rego = renegotiations.find(r => r.id === regoId);
      const res = await api.put(`/contract-renegotiations/${regoId}`, { ...rego, status, player_id: id });
      setRenegotiations(renegotiations.map(r => r.id === regoId ? res.data : r));
      toast.success('Status updated');
    } catch { toast.error('Failed to update'); }
  };

  const deleteRenegotiation = async (regoId) => {
    try {
      await api.delete(`/contract-renegotiations/${regoId}`);
      setRenegotiations(renegotiations.filter(r => r.id !== regoId));
      toast.success('Deleted');
    } catch { toast.error('Failed to delete'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="relative bg-black/60 backdrop-blur-xl border-b border-white/5 overflow-hidden">
        <div className="hero-glow"></div>
        <div className="relative z-10 p-6 md:p-8 lg:p-12">
          <Link
            to="/players"
            className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors duration-200"
          >
            <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            <span className="uppercase tracking-wide text-sm">Back to Players</span>
          </Link>
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="flex items-start gap-6">
              {/* Player Photo */}
              {player.photo ? (
                <img 
                  src={resolveFileUrl(player.photo)} 
                  alt={player.name}
                  className="w-24 h-24 md:w-32 md:h-32 rounded-lg object-cover border-2 border-white/10 flex-shrink-0"
                />
              ) : (
                <div className="w-24 h-24 md:w-32 md:h-32 rounded-lg bg-zinc-800 border-2 border-white/10 flex items-center justify-center flex-shrink-0">
                  <User className="w-12 h-12 text-zinc-600" />
                </div>
              )}
              
              <div>
                <h1 className="text-4xl md:text-5xl font-bold uppercase tracking-tight leading-none text-white mb-2">
                  {player.name}
                </h1>
                <p className="text-lg text-zinc-300">
                  {player.position}{player.position2 && `/${player.position2}`} • {player.nationality} • Age {player.age}
                  {player.preferred_foot && ` • ${player.preferred_foot} footed`}
                </p>
                {player.player_type && (
                  <span className="inline-block mt-2 px-3 py-1 bg-white/20 text-white text-sm font-bold uppercase rounded">
                    {player.player_type}
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-3 flex-wrap">
              <button
                onClick={() => setShowInvite(!showInvite)}
                data-testid="invite-player-button"
                className="inline-flex items-center gap-2 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 border border-blue-500/20 rounded-sm px-4 py-2 transition-all duration-200"
              >
                <UserPlus className="w-4 h-4" strokeWidth={1.5} />
                Invite to Portal
              </button>
              <Link
                to={`/players/${id}/edit`}
                data-testid="edit-player-button"
                className="inline-flex items-center gap-2 bg-white/10 text-white hover:bg-white/20 border border-white/5 rounded-sm px-4 py-2 transition-all duration-200"
              >
                <Edit className="w-4 h-4" strokeWidth={1.5} />
                Edit
              </Link>
              <button
                onClick={handleDelete}
                disabled={deleting}
                data-testid="delete-player-button"
                className="inline-flex items-center gap-2 bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20 rounded-sm px-4 py-2 transition-all duration-200 disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 md:p-8 lg:p-12">
        {/* Invite Panel */}
        {showInvite && (
          <div className="glass-card p-6 mb-6 border border-blue-500/20">
            <h3 className="text-lg font-bold uppercase tracking-tight text-white mb-3 flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-blue-400" />
              Invite Player to Portal
            </h3>
            {!inviteToken ? (
              <div className="flex gap-3 items-end">
                <div className="flex-1">
                  <label className="block text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">Player's Email Address</label>
                  <input
                    type="email"
                    data-testid="invite-email-input"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    className="w-full bg-zinc-900/50 border border-white/10 focus:border-white rounded-md px-4 py-3 text-white placeholder:text-zinc-600 outline-none"
                    placeholder="player@email.com"
                  />
                </div>
                <button
                  onClick={handleInvite}
                  disabled={inviting}
                  data-testid="send-invite-button"
                  className="px-6 py-3 bg-blue-500 text-white hover:bg-blue-600 font-bold uppercase tracking-wide rounded-sm transition-all disabled:opacity-50"
                >
                  {inviting ? 'Creating...' : 'Create Invite'}
                </button>
              </div>
            ) : (
              <div>
                <p className="text-sm text-zinc-400 mb-3">Share this invite code with {player.name}. They can use it on the login page to set up their account.</p>
                <div className="flex gap-2 items-center">
                  <code data-testid="invite-code-display" className="flex-1 bg-zinc-900 border border-white/10 rounded-md px-4 py-3 text-white font-mono text-sm break-all">{inviteToken}</code>
                  <button onClick={copyInviteCode} data-testid="copy-invite-button" className="px-4 py-3 bg-white/10 text-white hover:bg-white/20 rounded-sm transition-all">
                    <Copy className="w-5 h-5" />
                  </button>
                </div>
                <button onClick={() => { setShowInvite(false); setInviteToken(''); setInviteEmail(''); }} className="mt-3 text-sm text-zinc-500 hover:text-white">Close</button>
              </div>
            )}
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Clubs Contacted */}
            <div className="glass-card p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold uppercase tracking-tight text-white flex items-center gap-2">
                  <Shield className="w-5 h-5 text-blue-400" strokeWidth={1.5} />
                  Clubs Contacted
                </h3>
                <button onClick={() => setShowContactForm(!showContactForm)} data-testid="add-club-contact-button"
                  className="flex items-center gap-1 px-3 py-1.5 bg-white text-black hover:bg-zinc-200 font-bold uppercase text-xs rounded-sm transition-all">
                  <Plus className="w-3 h-3" /> Add
                </button>
              </div>
              {showContactForm && (
                <div className="p-4 bg-white/5 rounded-lg mb-4 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <select value={contactForm.club_id} onChange={(e) => setContactForm({ ...contactForm, club_id: e.target.value })}
                      className="bg-zinc-900/50 border border-white/10 focus:border-white rounded-md px-3 py-2 text-white outline-none text-sm">
                      <option value="">Select Club</option>
                      {clubs.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    <select value={contactForm.outcome} onChange={(e) => setContactForm({ ...contactForm, outcome: e.target.value })}
                      className="bg-zinc-900/50 border border-white/10 focus:border-white rounded-md px-3 py-2 text-white outline-none text-sm">
                      <option value="potential">Potential</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                      <option value="trials">Trials</option>
                    </select>
                    <input type="date" value={contactForm.contact_date} onChange={(e) => setContactForm({ ...contactForm, contact_date: e.target.value })}
                      className="bg-zinc-900/50 border border-white/10 focus:border-white rounded-md px-3 py-2 text-white outline-none text-sm" />
                  </div>
                  <input type="text" placeholder="Notes (optional)" value={contactForm.notes || ''} onChange={(e) => setContactForm({ ...contactForm, notes: e.target.value })}
                    className="w-full bg-zinc-900/50 border border-white/10 rounded-md px-3 py-2 text-white placeholder:text-zinc-600 outline-none text-sm" />
                  <div className="flex gap-2">
                    <button onClick={addClubContact} className="px-4 py-1.5 bg-white text-black font-bold text-xs uppercase rounded-sm">Save</button>
                    <button onClick={() => setShowContactForm(false)} className="px-4 py-1.5 bg-white/10 text-white text-xs rounded-sm">Cancel</button>
                  </div>
                </div>
              )}
              {clubContacts.length === 0 ? (
                <p className="text-zinc-500 text-sm">No clubs contacted yet.</p>
              ) : (
                <div className="space-y-2">
                  {clubContacts.map((contact) => {
                    const outcomeColors = {
                      yes: 'bg-green-500/20 text-green-400 border-green-500/30',
                      no: 'bg-red-500/20 text-red-400 border-red-500/30',
                      potential: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
                      trials: 'bg-blue-500/20 text-blue-400 border-blue-500/30'
                    };
                    const history = contact.notes_history || [];
                    const isEditing = editingNotes === contact.id;
                    const isExpanded = expandedHistory === contact.id;
                    return (
                      <div key={contact.id} className="p-3 bg-white/5 rounded-lg" data-testid={`club-contact-${contact.id}`}>
                        <div className="flex items-center gap-3 group">
                          <Shield className="w-4 h-4 text-blue-400 flex-shrink-0" />
                          <span className="text-white font-medium flex-1">{contact.club_name}</span>
                          {contact.contact_date && <span className="text-xs text-zinc-500 hidden md:block">{formatDate(contact.contact_date)}</span>}
                          <select value={contact.outcome} onChange={(e) => updateContactOutcome(contact.id, e.target.value)}
                            className={`px-2 py-1 rounded text-xs font-bold border outline-none cursor-pointer ${outcomeColors[contact.outcome] || outcomeColors.potential}`}>
                            <option value="potential">Potential</option>
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                            <option value="trials">Trials</option>
                          </select>
                          <button onClick={() => deleteClubContact(contact.id)} className="p-1 hover:bg-red-500/20 rounded text-zinc-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        {/* Notes section */}
                        <div className="mt-2 ml-7">
                          {isEditing ? (
                            <div className="flex gap-2 items-start">
                              <textarea
                                value={editNotesValue}
                                onChange={(e) => setEditNotesValue(e.target.value)}
                                rows={2}
                                autoFocus
                                data-testid={`edit-notes-input-${contact.id}`}
                                className="flex-1 bg-zinc-900/50 border border-[#FFFFFF]/50 rounded-md px-3 py-2 text-white outline-none text-sm resize-none"
                                placeholder="Add notes..."
                              />
                              <button onClick={() => saveContactNotes(contact.id)} data-testid={`save-notes-${contact.id}`}
                                className="p-1.5 bg-white text-black rounded hover:bg-zinc-200"><Check className="w-3.5 h-3.5" /></button>
                              <button onClick={() => setEditingNotes(null)}
                                className="p-1.5 bg-white/10 text-white rounded hover:bg-white/20"><X className="w-3.5 h-3.5" /></button>
                            </div>
                          ) : (
                            <div className="flex items-start gap-2">
                              <p className="text-xs text-zinc-400 flex-1">{contact.notes || <span className="italic text-zinc-600">No notes</span>}</p>
                              <button
                                onClick={() => { setEditingNotes(contact.id); setEditNotesValue(contact.notes || ''); }}
                                data-testid={`edit-notes-btn-${contact.id}`}
                                className="p-1 hover:bg-white/10 rounded text-zinc-500 hover:text-white transition-all flex-shrink-0"
                                title="Edit notes"
                              >
                                <Pencil className="w-3 h-3" />
                              </button>
                            </div>
                          )}
                          {/* Notes change history */}
                          {history.length > 0 && (
                            <div className="mt-2">
                              <button
                                onClick={() => setExpandedHistory(isExpanded ? null : contact.id)}
                                data-testid={`toggle-history-${contact.id}`}
                                className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-500 hover:text-zinc-300 transition-colors"
                              >
                                <Clock className="w-3 h-3" />
                                {history.length} change{history.length !== 1 ? 's' : ''}
                                {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                              </button>
                              {isExpanded && (
                                <div className="mt-2 space-y-1.5 border-l-2 border-zinc-700 pl-3">
                                  {[...history].reverse().map((h, i) => (
                                    <div key={i} className="text-[11px]" data-testid={`note-history-${contact.id}-${i}`}>
                                      <div className="flex items-center gap-2 text-zinc-500">
                                        <span className="font-medium text-zinc-400">{h.changed_by}</span>
                                        <span>{formatDate(h.changed_at)}</span>
                                      </div>
                                      <p className="text-zinc-500 line-through">{h.previous_notes || '(empty)'}</p>
                                      <p className="text-zinc-300">{h.new_notes || '(cleared)'}</p>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Completed Deals */}
            <div className="glass-card p-6" data-testid="completed-deals-section">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold uppercase tracking-tight text-white flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-orange-400" strokeWidth={1.5} />
                  Completed Deals
                </h3>
                <button onClick={() => setShowDealForm(!showDealForm)} data-testid="add-deal-button"
                  className="flex items-center gap-1 px-3 py-1.5 bg-white text-black hover:bg-zinc-200 font-bold uppercase text-xs rounded-sm transition-all">
                  <Plus className="w-3 h-3" /> Add Deal
                </button>
              </div>
              {showDealForm && (
                <div className="p-4 bg-white/5 rounded-lg mb-4 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <select value={dealForm.deal_type} onChange={(e) => setDealForm({ ...dealForm, deal_type: e.target.value })}
                      data-testid="deal-type-select"
                      className="bg-zinc-900/50 border border-white/10 focus:border-white rounded-md px-3 py-2 text-white outline-none text-sm">
                      <option value="summer_deal">Summer Deal</option>
                      <option value="winter_deal">Winter Deal</option>
                      <option value="loan">Loan</option>
                    </select>
                    <input type="text" placeholder="Club name *" value={dealForm.club_name}
                      onChange={(e) => setDealForm({ ...dealForm, club_name: e.target.value })}
                      data-testid="deal-club-input"
                      className="bg-zinc-900/50 border border-white/10 focus:border-white rounded-md px-3 py-2 text-white placeholder:text-zinc-600 outline-none text-sm" />
                    <input type="text" placeholder="Notes (optional)" value={dealForm.notes}
                      onChange={(e) => setDealForm({ ...dealForm, notes: e.target.value })}
                      data-testid="deal-notes-input"
                      className="bg-zinc-900/50 border border-white/10 focus:border-white rounded-md px-3 py-2 text-white placeholder:text-zinc-600 outline-none text-sm" />
                  </div>
                  <button onClick={addDeal} data-testid="save-deal-button"
                    className="bg-white text-black hover:bg-zinc-200 font-bold uppercase text-xs px-4 py-2 rounded-sm">Save Deal</button>
                </div>
              )}
              {completedDeals.length === 0 ? (
                <p className="text-zinc-500 text-sm">No completed deals yet.</p>
              ) : (
                <div className="space-y-2">
                  {completedDeals.map(deal => {
                    const typeConfig = {
                      summer_deal: { label: 'Summer Deal', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
                      winter_deal: { label: 'Winter Deal', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
                      loan: { label: 'Loan', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' }
                    };
                    const cfg = typeConfig[deal.deal_type] || typeConfig.summer_deal;
                    return (
                      <div key={deal.id} className="p-3 bg-white/5 rounded-lg group" data-testid={`deal-${deal.id}`}>
                        <div className="flex items-center gap-3">
                          <span className={`px-2 py-1 rounded text-xs font-bold border ${cfg.color}`}>{cfg.label}</span>
                          <span className="text-white font-medium flex-1">{deal.club_name}</span>
                          {deal.notes && <span className="text-xs text-zinc-400 hidden lg:block max-w-[200px] truncate" title={deal.notes}>{deal.notes}</span>}
                          <span className="text-xs text-zinc-500">{formatDate(deal.created_at)}</span>
                          <button onClick={() => deleteDeal(deal.id)} className="p-1 hover:bg-red-500/20 rounded text-zinc-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Fixture History */}
            <div className="glass-card p-6">
              <h3 className="text-xl font-bold uppercase tracking-tight text-white flex items-center gap-2 mb-4">
                <Calendar className="w-5 h-5 text-blue-400" strokeWidth={1.5} />
                Fixture History
              </h3>
              {playerFixtures.length === 0 ? (
                <p className="text-zinc-500 text-sm">No fixtures linked to this player.</p>
              ) : (
                <div className="space-y-2">
                  {playerFixtures.map((f) => {
                    const isPast = f.date < new Date().toISOString().split('T')[0];
                    return (
                      <div key={f.id} className={`p-3 bg-white/5 rounded-lg flex items-center gap-3 ${isPast ? 'opacity-60' : ''}`}>
                        <div className="w-12 text-center flex-shrink-0">
                          <p className="text-lg font-bold text-white tabular-nums">{f.date ? new Date(f.date).getDate() : ''}</p>
                          <p className="text-[9px] uppercase tracking-widest text-zinc-500">
                            {f.date ? new Date(f.date).toLocaleDateString('en-GB', { month: 'short' }) : ''}
                          </p>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-white uppercase">{f.home_team} <span className="text-zinc-500 font-normal">vs</span> {f.away_team}</p>
                          <div className="flex items-center gap-2 text-xs text-zinc-400 mt-0.5">
                            {f.time && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{f.time}</span>}
                            {f.ground && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{f.ground}</span>}
                            {f.competition && <span className="flex items-center gap-1"><Shield className="w-3 h-3" />{f.competition}</span>}
                          </div>
                        </div>
                        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${isPast ? 'bg-zinc-500/20 text-zinc-400' : 'bg-blue-500/20 text-blue-400'}`}>
                          {isPast ? 'Played' : 'Upcoming'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Career Path */}
            <div className="glass-card p-6" data-testid="career-path-section">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold uppercase tracking-tight text-white flex items-center gap-2">
                  <Target className="w-5 h-5 text-green-400" strokeWidth={1.5} />
                  Career Path
                </h3>
                {!editingCareer ? (
                  <button onClick={() => setEditingCareer(true)} data-testid="edit-career-path-btn"
                    className="flex items-center gap-1 px-3 py-1.5 bg-white text-black hover:bg-zinc-200 font-bold uppercase text-xs rounded-sm transition-all">
                    <Pencil className="w-3 h-3" /> Edit
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button onClick={saveCareerPath} data-testid="save-career-path-btn"
                      className="flex items-center gap-1 px-3 py-1.5 bg-white text-black hover:bg-zinc-200 font-bold uppercase text-xs rounded-sm transition-all">
                      <Check className="w-3 h-3" /> Save
                    </button>
                    <button onClick={() => { setEditingCareer(false); setCareerForm({ career_path_1yr: player?.career_path_1yr || '', career_path_2yr: player?.career_path_2yr || '', career_path_3yr: player?.career_path_3yr || '' }); }}
                      className="flex items-center gap-1 px-3 py-1.5 bg-white/10 text-white hover:bg-white/20 font-bold uppercase text-xs rounded-sm transition-all">
                      <X className="w-3 h-3" /> Cancel
                    </button>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-white/5 rounded-lg border border-white/5">
                  <h4 className="text-xs font-bold uppercase tracking-widest text-green-400 mb-3">1-Year Goal</h4>
                  {editingCareer ? (
                    <textarea value={careerForm.career_path_1yr} onChange={(e) => setCareerForm({ ...careerForm, career_path_1yr: e.target.value })}
                      data-testid="career-1yr-input" rows="4" placeholder="What club level is this player aiming for in 1 year?"
                      className="w-full bg-zinc-900/50 border border-white/10 focus:border-white rounded-md px-3 py-2 text-white placeholder:text-zinc-600 outline-none text-sm resize-none" />
                  ) : (
                    <p className="text-zinc-300 text-sm whitespace-pre-wrap">{player?.career_path_1yr || <span className="text-zinc-600 italic">No 1-year career path set</span>}</p>
                  )}
                </div>
                <div className="p-4 bg-white/5 rounded-lg border border-white/5">
                  <h4 className="text-xs font-bold uppercase tracking-widest text-yellow-400 mb-3">2-Year Goal</h4>
                  {editingCareer ? (
                    <textarea value={careerForm.career_path_2yr} onChange={(e) => setCareerForm({ ...careerForm, career_path_2yr: e.target.value })}
                      data-testid="career-2yr-input" rows="4" placeholder="What club level is this player aiming for in 2 years?"
                      className="w-full bg-zinc-900/50 border border-white/10 focus:border-white rounded-md px-3 py-2 text-white placeholder:text-zinc-600 outline-none text-sm resize-none" />
                  ) : (
                    <p className="text-zinc-300 text-sm whitespace-pre-wrap">{player?.career_path_2yr || <span className="text-zinc-600 italic">No 2-year career path set</span>}</p>
                  )}
                </div>
                <div className="p-4 bg-white/5 rounded-lg border border-white/5">
                  <h4 className="text-xs font-bold uppercase tracking-widest text-blue-400 mb-3">3-Year Goal</h4>
                  {editingCareer ? (
                    <textarea value={careerForm.career_path_3yr} onChange={(e) => setCareerForm({ ...careerForm, career_path_3yr: e.target.value })}
                      data-testid="career-3yr-input" rows="4" placeholder="What club level is this player aiming for in 3 years?"
                      className="w-full bg-zinc-900/50 border border-white/10 focus:border-white rounded-md px-3 py-2 text-white placeholder:text-zinc-600 outline-none text-sm resize-none" />
                  ) : (
                    <p className="text-zinc-300 text-sm whitespace-pre-wrap">{player?.career_path_3yr || <span className="text-zinc-600 italic">No 3-year career path set</span>}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Player Media */}
            <div className="glass-card p-6" data-testid="player-media-section">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold uppercase tracking-tight text-white flex items-center gap-2">
                  <Camera className="w-5 h-5 text-purple-400" strokeWidth={1.5} />
                  Media
                </h3>
                <button onClick={() => { setShowMediaForm(!showMediaForm); setEditingMedia(null); setMediaForm({ name: '', url: '', media_type: 'clip_link' }); }} data-testid="add-media-btn"
                  className="flex items-center gap-1 px-3 py-1.5 bg-white text-black hover:bg-zinc-200 font-bold uppercase text-xs rounded-sm transition-all">
                  <Plus className="w-3 h-3" /> Add
                </button>
              </div>
              {showMediaForm && (
                <div className="mb-4 p-4 bg-white/5 rounded-lg border border-white/10 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <select value={mediaForm.media_type} onChange={(e) => setMediaForm({ ...mediaForm, media_type: e.target.value })} data-testid="media-type-select"
                      className="bg-zinc-900 border border-white/10 rounded-md px-3 py-2 text-white text-sm outline-none">
                      <option value="clip_link">Clip Link</option>
                      <option value="photo">Photo Link</option>
                      <option value="video">Video Link</option>
                    </select>
                    <input value={mediaForm.name} onChange={(e) => setMediaForm({ ...mediaForm, name: e.target.value })} data-testid="media-name-input"
                      placeholder="Name / Description" className="bg-zinc-900 border border-white/10 rounded-md px-3 py-2 text-white text-sm placeholder:text-zinc-600 outline-none" />
                    <input value={mediaForm.url} onChange={(e) => setMediaForm({ ...mediaForm, url: e.target.value })} data-testid="media-url-input"
                      placeholder="URL" className="bg-zinc-900 border border-white/10 rounded-md px-3 py-2 text-white text-sm placeholder:text-zinc-600 outline-none" />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={editingMedia ? updateMedia : addMedia} data-testid="save-media-btn"
                      className="px-4 py-2 bg-white text-black hover:bg-zinc-200 font-bold uppercase text-xs rounded-sm transition-all">
                      {editingMedia ? 'Update' : 'Add Media'}
                    </button>
                    <button onClick={() => { setShowMediaForm(false); setEditingMedia(null); }}
                      className="px-4 py-2 bg-white/10 text-white hover:bg-white/20 text-xs rounded-sm transition-all">Cancel</button>
                  </div>
                </div>
              )}
              {playerMedia.length > 0 ? (
                <div className="space-y-2">
                  {playerMedia.map((m) => (
                    <div key={m.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg border border-white/5 group hover:border-white/10 transition-colors" data-testid={`media-item-${m.id}`}>
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        {m.media_type === 'video' ? <Video className="w-4 h-4 text-purple-400 flex-shrink-0" /> :
                         m.media_type === 'photo' ? <Camera className="w-4 h-4 text-blue-400 flex-shrink-0" /> :
                         <LinkIcon className="w-4 h-4 text-green-400 flex-shrink-0" />}
                        <div className="min-w-0">
                          <p className="text-white text-sm font-medium truncate">{m.name || 'Untitled'}</p>
                          {m.url && <a href={m.url} target="_blank" rel="noopener noreferrer" className="text-zinc-500 text-xs truncate hover:text-white block">{m.url}</a>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => shareMedia(m)} data-testid={`share-media-${m.id}`} title="Share / Forward"
                          className="p-1.5 text-zinc-400 hover:text-white transition-colors"><Share2 className="w-4 h-4" /></button>
                        <button onClick={() => { setEditingMedia(m); setMediaForm({ name: m.name || '', url: m.url || '', media_type: m.media_type || 'clip_link' }); setShowMediaForm(true); }}
                          data-testid={`edit-media-${m.id}`} title="Edit"
                          className="p-1.5 text-zinc-400 hover:text-blue-400 transition-colors"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => deleteMedia(m.id)} data-testid={`delete-media-${m.id}`} title="Delete"
                          className="p-1.5 text-zinc-400 hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-zinc-500 text-sm">No media uploaded yet.</p>
              )}
            </div>

            {/* Contract Renegotiations */}
            <div className="glass-card p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold uppercase tracking-tight text-white flex items-center gap-2">
                  <RefreshCw className="w-5 h-5 text-orange-400" strokeWidth={1.5} />
                  Negotiate Club Contract
                </h3>
                <button onClick={() => setShowRegoForm(!showRegoForm)} data-testid="add-renegotiation-button"
                  className="flex items-center gap-1 px-3 py-1.5 bg-white text-black hover:bg-zinc-200 font-bold uppercase text-xs rounded-sm transition-all">
                  <Plus className="w-3 h-3" /> Add
                </button>
              </div>
              {showRegoForm && (
                <div className="p-4 bg-white/5 rounded-lg mb-4 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input type="text" placeholder="Club name *" value={regoForm.club_name}
                      onChange={(e) => setRegoForm({ ...regoForm, club_name: e.target.value })}
                      data-testid="rego-club-name-input"
                      className="bg-zinc-900/50 border border-white/10 focus:border-white rounded-md px-3 py-2 text-white placeholder:text-zinc-600 outline-none text-sm" />
                    <select value={regoForm.status} onChange={(e) => setRegoForm({ ...regoForm, status: e.target.value })}
                      className="bg-zinc-900/50 border border-white/10 focus:border-white rounded-md px-3 py-2 text-white outline-none text-sm">
                      <option value="start_conversation">Start Conversation</option>
                      <option value="in_progress">In Progress</option>
                      <option value="agreed">Agreed</option>
                      <option value="not_accepted">Not Accepted</option>
                    </select>
                  </div>
                  <input type="text" placeholder="Notes (optional)" value={regoForm.notes || ''}
                    onChange={(e) => setRegoForm({ ...regoForm, notes: e.target.value })}
                    className="w-full bg-zinc-900/50 border border-white/10 rounded-md px-3 py-2 text-white placeholder:text-zinc-600 outline-none text-sm" />
                  <div className="flex gap-2">
                    <button onClick={addRenegotiation} data-testid="save-renegotiation-button"
                      className="px-4 py-1.5 bg-white text-black font-bold text-xs uppercase rounded-sm">Save</button>
                    <button onClick={() => setShowRegoForm(false)} className="px-4 py-1.5 bg-white/10 text-white text-xs rounded-sm">Cancel</button>
                  </div>
                </div>
              )}
              {renegotiations.length === 0 ? (
                <p className="text-zinc-500 text-sm">No contract negotiations.</p>
              ) : (
                <div className="space-y-3">
                  {renegotiations.map((rego) => {
                    const statusColors = {
                      start_conversation: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
                      in_progress: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
                      agreed: 'bg-green-500/20 text-green-400 border-green-500/30',
                      not_accepted: 'bg-red-500/20 text-red-400 border-red-500/30'
                    };
                    const statusLabels = { start_conversation: 'Start Conversation', in_progress: 'In Progress', agreed: 'Agreed', not_accepted: 'Not Accepted' };
                    const history = rego.history || [];
                    return (
                      <div key={rego.id} className="bg-white/5 rounded-lg overflow-hidden" data-testid={`rego-item-${rego.id}`}>
                        <div className="flex items-center gap-3 p-3 group">
                          <RefreshCw className="w-4 h-4 text-orange-400 flex-shrink-0" />
                          <span className="text-white font-medium flex-1">{rego.club_name}</span>
                          {rego.notes && <span className="text-xs text-zinc-400 hidden lg:block max-w-[150px] truncate" title={rego.notes}>{rego.notes}</span>}
                          <select value={rego.status} onChange={(e) => updateRegoStatus(rego.id, e.target.value)}
                            data-testid={`rego-status-${rego.id}`}
                            className={`px-2 py-1 rounded text-xs font-bold border outline-none cursor-pointer ${statusColors[rego.status] || statusColors.start_conversation}`}>
                            <option value="start_conversation">Start Conversation</option>
                            <option value="in_progress">In Progress</option>
                            <option value="agreed">Agreed</option>
                            <option value="not_accepted">Not Accepted</option>
                          </select>
                          <button onClick={() => deleteRenegotiation(rego.id)} className="p-1 hover:bg-red-500/20 rounded text-zinc-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        {history.length > 0 && (
                          <div className="px-3 pb-3 pt-1 border-t border-white/5">
                            <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Timeline</p>
                            <div className="relative pl-4">
                              <div className="absolute left-[7px] top-1 bottom-1 w-px bg-white/10"></div>
                              {history.map((h, i) => {
                                const dotColor = { start_conversation: 'bg-blue-400', in_progress: 'bg-yellow-400', agreed: 'bg-green-400', not_accepted: 'bg-red-400' };
                                return (
                                  <div key={i} className="relative flex items-start gap-3 pb-2 last:pb-0">
                                    <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 -ml-[3px] ${dotColor[h.status] || 'bg-zinc-400'}`}></div>
                                    <div className="flex-1 min-w-0">
                                      <span className="text-xs text-white font-medium">{h.label || statusLabels[h.status] || h.status}</span>
                                      <span className="text-[10px] text-zinc-500 ml-2">{h.changed_by} &bull; {h.timestamp ? formatDate(h.timestamp) : ''}</span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Notes */}
            {player.performance_notes && (
              <div className="glass-card p-6">
                <h3 className="text-xl font-bold uppercase tracking-tight text-white mb-4">Notes</h3>
                <p className="text-zinc-300 leading-relaxed">{player.performance_notes}</p>
              </div>
            )}

            {/* Injury History */}
            {player.injury_history && (
              <div className="glass-card p-6">
                <h3 className="text-xl font-bold uppercase tracking-tight text-white mb-4">Injury History</h3>
                <p className="text-zinc-300 leading-relaxed">{player.injury_history}</p>
              </div>
            )}

            {/* Website Links */}
            {player.links && player.links.length > 0 && (
              <div className="glass-card p-6">
                <h3 className="text-xl font-bold uppercase tracking-tight text-white mb-4 flex items-center gap-2">
                  <LinkIcon className="w-5 h-5 text-blue-400" strokeWidth={1.5} />
                  Links
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {player.links.map((link, index) => (
                    <a
                      key={index}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors group"
                    >
                      <LinkIcon className="w-4 h-4 text-blue-400" />
                      <span className="text-white font-medium flex-1">{link.name}</span>
                      <ExternalLink className="w-4 h-4 text-zinc-500 group-hover:text-blue-400" />
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* PDF Documents */}
            {player.documents && player.documents.length > 0 && (
              <div className="glass-card p-6">
                <h3 className="text-xl font-bold uppercase tracking-tight text-white mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-red-400" strokeWidth={1.5} />
                  Documents
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {player.documents.map((doc, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        if (doc.url) {
                          window.open(resolveFileUrl(doc.url), '_blank');
                        } else if (doc.data) {
                          const link = document.createElement('a');
                          link.href = doc.data;
                          link.download = doc.filename || `${doc.name}.pdf`;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }
                      }}
                      className="flex items-center gap-3 p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors group text-left"
                    >
                      <FileText className="w-4 h-4 text-red-400" />
                      <span className="text-white font-medium flex-1">{doc.name}</span>
                      <Download className="w-4 h-4 text-zinc-500 group-hover:text-red-400" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Info */}
            {(player.email || player.phone) && (
              <div className="glass-card p-6">
                <h3 className="text-xl font-bold uppercase tracking-tight text-white mb-4 flex items-center gap-2">
                  <Mail className="w-5 h-5 text-blue-400" strokeWidth={1.5} />
                  Contact
                </h3>
                <div className="space-y-3">
                  {player.email && (
                    <a 
                      href={`mailto:${player.email}`}
                      className="flex items-center gap-3 p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                    >
                      <Mail className="w-4 h-4 text-blue-400" />
                      <span className="text-white text-sm">{player.email}</span>
                    </a>
                  )}
                  {player.phone && (
                    <a 
                      href={`tel:${player.phone}`}
                      className="flex items-center gap-3 p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                    >
                      <Phone className="w-4 h-4 text-green-400" />
                      <span className="text-white text-sm">{player.phone}</span>
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Current Club */}
            {player.current_club && (
              <div className="glass-card p-6">
                <h3 className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Current Club</h3>
                <p className="text-xl font-bold text-white">{player.current_club}</p>
              </div>
            )}

            {/* Contract Info */}
            <div className="glass-card p-6">
              <h3 className="text-xl font-bold uppercase tracking-tight text-white mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-400" strokeWidth={1.5} />
                Contract Details
              </h3>
              <div className="space-y-4">
                {player.salary && (
                  <div className="p-4 bg-white/5 rounded-lg">
                    <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Salary</p>
                    <p className="text-2xl font-bold tabular-nums text-green-400">
                      £{player.salary.toLocaleString()}
                      <span className="text-sm text-zinc-400 ml-1">/{player.salary_frequency || 'weekly'}</span>
                    </p>
                  </div>
                )}
                {player.agent_fee && (
                  <div className="p-4 bg-white/5 rounded-lg">
                    <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Agent Fee</p>
                    <p className="text-xl font-bold tabular-nums text-white">
                      £{player.agent_fee.toLocaleString()}
                    </p>
                  </div>
                )}
                {(player.addons_description || player.addons_value) && (
                  <div className="p-4 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                    <p className="text-xs uppercase tracking-widest text-orange-400 mb-2">Contract Add-ons</p>
                    {player.addons_description && (
                      <p className="text-sm text-zinc-300 mb-2">{player.addons_description}</p>
                    )}
                    {player.addons_value && (
                      <p className="text-lg font-bold tabular-nums text-orange-400">
                        £{player.addons_value.toLocaleString()} total
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Contract Info */}
            <div className="glass-card p-6 space-y-4">
              {player.contract_end_date && (
                <div>
                  <h3 className="text-xs uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-2">
                    <Calendar className="w-4 h-4" strokeWidth={1.5} />
                    Contract End Date
                  </h3>
                  <p className="text-lg font-bold text-white">{formatDate(player.contract_end_date)}</p>
                </div>
              )}
              {player.representation_contract_end && (
                <div className="pt-4 border-t border-white/10">
                  <h3 className="text-xs uppercase tracking-widest text-orange-400 mb-2 flex items-center gap-2">
                    <Calendar className="w-4 h-4" strokeWidth={1.5} />
                    Agency Contract Ends
                  </h3>
                  <p className="text-lg font-bold text-orange-400">{formatDate(player.representation_contract_end)}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlayerDetail;
